import React, { useRef, useState } from 'react';
import {
  Banner,
  CondensedLabel,
  Flashy,
  Form,
  GroupedElements,
  Gutter,
  Icon,
  IconButton,
  Layout,
  List,
  ListItem,
  Textarea,
  Tooltip,
  Well,
} from '@axiom/ui';

import { ChatEmailFormSchema } from '../../../schema/spark/chat-email-form-schema';
import { ChatCopyButton } from '../ChatCopyButton/ChatCopyButton';
import type { ChatEmailDraft } from '../../types/chat-email.types';
import { useSparkSession } from '../../hooks/useSparkSession';
import { ChatEmailUtil } from '../../utils/chat-email-util';

import { ChatEmailField } from './ChatEmailField';
import { ChatEmailStartButton } from './ChatEmailStartButton';

const COPY_FAILED_NOTICE =
  "The body couldn't be copied, so it isn't on your clipboard. Use Copy body, then paste it into the email.";

export type ChatEmailProps = {
  email: ChatEmailDraft;
  suffix: string;
};

export const ChatEmail = ({ email, suffix }: ChatEmailProps) => {
  const { askAboutDraft } = useSparkSession();
  const [isEditing, setIsEditing] = useState(false);
  const [bodyCopyFailed, setBodyCopyFailed] = useState(false);

  // Refs wrap the value alone: ChatCopyButton copies the whole node, so a wider ref would include the label.
  const toRef = useRef<HTMLDivElement>(null);
  const ccRef = useRef<HTMLDivElement>(null);
  const bccRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  return (
    <Form
      initialValues={{ body: email.body }}
      name={`CHAT_EMAIL_FORM_${suffix}`}
      schema={ChatEmailFormSchema}
    >
      {({ values }) => {
        const body = values.body ?? '';
        const isBodyEdited = body !== email.body;

        return (
          <List
            background="light"
            condensed
            name={`CHAT_EMAIL_${suffix}`}
            variation="outline"
          >
            <ListItem>
              <Layout position="space-between middle">
                <Layout horizontalGutter="8px" inline position="middle">
                  <CondensedLabel>Email</CondensedLabel>
                </Layout>
                {ChatEmailUtil.hasMailtoFields({ email }) ? (
                  <ChatEmailStartButton
                    bodyRef={bodyRef}
                    email={{
                      bcc: email.bcc,
                      body,
                      cc: email.cc,
                      from: email.from,
                      subject: email.subject,
                      to: email.to,
                    }}
                    suffix={suffix}
                    onBodyCopy={({ copied }) => {
                      setBodyCopyFailed(!copied);
                    }}
                  />
                ) : null}
              </Layout>
            </ListItem>

            {email.to ? (
              <ChatEmailField
                contentRef={toRef}
                suffix={suffix}
                label="TO"
                name="TO"
                value={email.to}
              />
            ) : null}

            {email.cc ? (
              <ChatEmailField
                contentRef={ccRef}
                suffix={suffix}
                label="CC"
                name="CC"
                value={email.cc}
              />
            ) : null}

            {email.bcc ? (
              <ChatEmailField
                contentRef={bccRef}
                suffix={suffix}
                label="BCC"
                name="BCC"
                value={email.bcc}
              />
            ) : null}

            {email.subject ? (
              <ChatEmailField
                contentRef={subjectRef}
                suffix={suffix}
                label="SUBJECT"
                name="SUBJECT"
                value={email.subject}
              />
            ) : null}

            {email.body ? (
              <ListItem>
                <Layout position="space-between middle">
                  <CondensedLabel>BODY</CondensedLabel>
                  <GroupedElements
                    disableHover
                    divider
                    name={`EMAIL_BODY_ACTIONS_${suffix}`}
                    outlined
                  >
                    {isEditing || isBodyEdited ? (
                      <Tooltip
                        anchor={
                          <IconButton
                            icon="intelligence"
                            name={`ASK_ABOUT_EMAIL_BODY_${suffix}`}
                            onClick={() => {
                              askAboutDraft({ body });
                            }}
                            pattern="secondary"
                            size="small"
                            variation="minimal"
                          />
                        }
                        fade
                        hideClose
                        name={`ASK_ABOUT_EMAIL_BODY_EXPLANATION_${suffix}`}
                        position="top"
                        variation="minimal"
                      >
                        Ask about this version
                      </Tooltip>
                    ) : null}
                    <Tooltip
                      anchor={
                        <IconButton
                          icon={isEditing ? 'checkmark' : 'pencil'}
                          name={`EDIT_EMAIL_BODY_${suffix}`}
                          onClick={() => {
                            setIsEditing(editing => !editing);
                          }}
                          pattern="secondary"
                          size="small"
                          variation="minimal"
                        />
                      }
                      fade
                      hideClose
                      name={`EDIT_EMAIL_BODY_EXPLANATION_${suffix}`}
                      position="top"
                      variation="minimal"
                    >
                      {isEditing ? 'Done' : 'Edit body'}
                    </Tooltip>
                    <ChatCopyButton
                      contentRef={bodyRef}
                      formattedOnClick
                      label="Copy body"
                      markdown={body}
                      name={`COPY_EMAIL_BODY_${suffix}`}
                    />
                  </GroupedElements>
                </Layout>
                {isEditing || isBodyEdited ? (
                  <Gutter bottom="8px" top="16px">
                    <Banner
                      impact="low"
                      name={`EMAIL_BODY_NOT_SAVED_${suffix}`}
                      type="info"
                    >
                      <Flashy bold>Quick Edit:</Flashy> Add edits back to chat
                      using the <Icon name="intelligence" /> button. Edits below
                      do <Flashy underline>not</Flashy> automatically get saved
                      or persist across chats.
                    </Banner>
                  </Gutter>
                ) : null}
                {bodyCopyFailed ? (
                  <Gutter bottom="8px" top="16px">
                    <Banner
                      impact="low"
                      name={`EMAIL_BODY_COPY_FAILED_${suffix}`}
                      onDismiss={() => {
                        setBodyCopyFailed(false);
                      }}
                      type="warning"
                    >
                      {COPY_FAILED_NOTICE}
                    </Banner>
                  </Gutter>
                ) : null}

                <Well
                  background="light"
                  border={isEditing}
                  name={`EMAIL_BODY_WELL_${suffix}`}
                >
                  <div data-test={`CHAT_EMAIL_BODY_${suffix}`} ref={bodyRef}>
                    <Textarea
                      formatting="modern"
                      name="body"
                      readonly={!isEditing}
                      variation="seamless"
                    />
                  </div>
                </Well>
              </ListItem>
            ) : null}
          </List>
        );
      }}
    </Form>
  );
};
